import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const SORTS = [
    { v: "featured", t: "Featured" },
    { v: "price_asc", t: "Price: low → high" },
    { v: "price_desc", t: "Price: high → low" },
    { v: "name", t: "Alphabetical" },
];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const activeCat = params.get("category") || "";
    const q = params.get("q") || "";

    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState([]);
    const [sort, setSort] = useState("featured");
    const [search, setSearch] = useState(q);

    useEffect(() => {
        api.get("/categories").then((r) => setCats(r.data));
    }, []);

    useEffect(() => {
        const p = new URLSearchParams();
        if (activeCat) p.append("category", activeCat);
        if (q) p.append("q", q);
        api.get(`/products?${p.toString()}`).then((r) =>
            setProducts(r.data),
        );
    }, [activeCat, q]);

    const sorted = useMemo(() => {
        const arr = [...products];
        if (sort === "price_asc") arr.sort((a, b) => a.price - b.price);
        else if (sort === "price_desc")
            arr.sort((a, b) => b.price - a.price);
        else if (sort === "name")
            arr.sort((a, b) => a.name.localeCompare(b.name));
        else arr.sort((a, b) => (b.best_seller ? 1 : 0) - (a.best_seller ? 1 : 0));
        return arr;
    }, [products, sort]);

    const pickCat = (slug) => {
        setParams((prev) => {
            const p = new URLSearchParams(prev);
            if (slug) p.set("category", slug);
            else p.delete("category");
            return p;
        });
    };

    const submitSearch = (e) => {
        e.preventDefault();
        setParams((prev) => {
            const p = new URLSearchParams(prev);
            if (search) p.set("q", search);
            else p.delete("q");
            return p;
        });
    };

    return (
        <div data-testid="shop-page">
            {/* Header */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 pb-10"
            >
                <div className="overline mb-4">The Collection</div>
                <h1
                    className="font-serif text-5xl sm:text-6xl tracking-tight"
                    style={{ color: "var(--vl-text)" }}
                    data-testid="shop-title"
                >
                    Shop{" "}
                    <em
                        style={{
                            color: "var(--vl-rose)",
                            fontStyle: "italic",
                        }}
                    >
                        Veloura.
                    </em>
                </h1>
                <p
                    className="mt-5 max-w-xl"
                    style={{
                        color: "var(--vl-muted)",
                        lineHeight: 1.8,
                    }}
                >
                    A considered catalogue. Every piece body-safe, every
                    formula dermatologist-tested, every box discreet.
                </p>
            </section>

            {/* Filters */}
            <section
                className="max-w-[1440px] mx-auto px-6 md:px-12 pb-10"
                data-testid="shop-filters"
            >
                <div className="flex flex-wrap gap-2 mb-6">
                    <button
                        onClick={() => pickCat("")}
                        data-testid="filter-all"
                        className="text-[11px] tracking-[0.22em] uppercase px-4 py-2"
                        style={{
                            border: "1px solid var(--vl-border)",
                            color: !activeCat
                                ? "var(--vl-bg)"
                                : "var(--vl-text)",
                            background: !activeCat
                                ? "var(--vl-rose)"
                                : "transparent",
                        }}
                    >
                        All
                    </button>
                    {cats.map((c) => (
                        <button
                            key={c.slug}
                            onClick={() => pickCat(c.slug)}
                            data-testid={`filter-${c.slug}`}
                            className="text-[11px] tracking-[0.22em] uppercase px-4 py-2"
                            style={{
                                border: "1px solid var(--vl-border)",
                                color:
                                    activeCat === c.slug
                                        ? "var(--vl-bg)"
                                        : "var(--vl-text)",
                                background:
                                    activeCat === c.slug
                                        ? "var(--vl-rose)"
                                        : "transparent",
                            }}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-5">
                    <form
                        onSubmit={submitSearch}
                        className="w-full sm:w-80"
                        data-testid="shop-search-form"
                    >
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, tag…"
                            className="vl-input"
                            data-testid="shop-search-input"
                        />
                    </form>
                    <div className="flex items-center gap-3">
                        <span className="overline">Sort</span>
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            data-testid="shop-sort"
                            className="bg-transparent border px-3 py-2 text-sm"
                            style={{
                                borderColor: "var(--vl-border)",
                                color: "var(--vl-text)",
                            }}
                        >
                            {SORTS.map((s) => (
                                <option
                                    key={s.v}
                                    value={s.v}
                                    style={{ background: "var(--vl-bg)" }}
                                >
                                    {s.t}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-32">
                {sorted.length === 0 ? (
                    <div
                        className="py-20 text-center"
                        style={{ color: "var(--vl-muted)" }}
                        data-testid="shop-empty"
                    >
                        No pieces match your filters.
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
                        data-testid="shop-grid"
                    >
                        {sorted.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
